// Item type identifiers - extensible enum pattern
export const ItemType = {
    NONE: 0,
    // Raw materials (1-99)
    WOOD: 1,
    STONE: 2,
    IRON_ORE: 3,
    GOLD_ORE: 4,
    COPPER_ORE: 5,
    COAL: 6,
    TIN_ORE: 7,
    SILVER_ORE: 8,
    MITHRIL_ORE: 9,
    STARMETAL_ORE: 10,
    BONE: 11, // Monster drops
    // Processed materials (100-199)
    PLANK: 100,
    IRON_BAR: 101,
    GOLD_BAR: 102,
    COPPER_BAR: 103,
    STEEL_BAR: 104,
    TIN_BAR: 105,
    BRONZE_BAR: 106,
    SILVER_BAR: 107,
    MITHRIL_BAR: 108,
    STARMETAL_BAR: 109,
    // Food (200-299)
    BERRIES: 200,
    RAW_MEAT: 201,
    COOKED_MEAT: 202,
    BREAD: 203,
    // Fish - Raw (204-208)
    RAW_MINNOW: 204,
    RAW_TROUT: 205,
    RAW_COD: 206,
    RAW_SALMON: 207,
    RAW_SWAMP_EEL: 208,
    // Fish - Cooked (209-213)
    COOKED_MINNOW: 209,
    COOKED_TROUT: 210,
    COOKED_COD: 211,
    COOKED_SALMON: 212,
    COOKED_EEL: 213,
    // Fish - Advanced (214)
    FISH_STEW: 214,
    // Fish - Specialty (215-216)
    RAW_SHRIMP: 215,
    COOKED_SHRIMP: 216,
    // Tools (300-399)
    WOODEN_AXE: 300,
    STONE_AXE: 301,
    IRON_AXE: 302,
    BRONZE_AXE: 303,
    STEEL_AXE: 304,
    MITHRIL_AXE: 305,
    STARMETAL_AXE: 306,
    WOODEN_PICKAXE: 310,
    STONE_PICKAXE: 311,
    IRON_PICKAXE: 312,
    BRONZE_PICKAXE: 313,
    STEEL_PICKAXE: 314,
    SILVER_PICKAXE: 315,
    MITHRIL_PICKAXE: 316,
    STARMETAL_PICKAXE: 317,
    // Fishing Rods (320-323)
    WOODEN_FISHING_ROD: 320,
    COPPER_FISHING_ROD: 321,
    IRON_FISHING_ROD: 322,
    STEEL_FISHING_ROD: 323,
    // Weapons (400-499)
    WOODEN_SWORD: 400,
    STONE_SWORD: 401,
    IRON_SWORD: 402,
    // Ranged Weapons (500-549)
    WOODEN_BOW: 500,
    STONE_BOW: 501,
    IRON_BOW: 502,
    BRONZE_BOW: 503,
    STEEL_BOW: 504,
    SILVER_BOW: 505,
    MITHRIL_BOW: 506,
    STARMETAL_BOW: 507,
    FLINTLOCK_PISTOL: 520,
    // Ammunition (550-599)
    WOODEN_ARROW: 550,
    STONE_ARROW: 551,
    IRON_ARROW: 552,
    BRONZE_ARROW: 553,
    STEEL_ARROW: 554,
    SILVER_ARROW: 555,
    MITHRIL_ARROW: 556,
    STARMETAL_ARROW: 557,
    LEAD_BALL: 570,
    GUNPOWDER: 571,
    // Crafting Materials (600-699)
    CLOTH: 600,
};
// Item categories for grouping behavior
export const ItemCategory = {
    NONE: 0,
    RAW_MATERIAL: 1,
    PROCESSED_MATERIAL: 2,
    FOOD: 3,
    TOOL: 4,
    WEAPON: 5,
    ARMOR: 6,
    CONSUMABLE: 7,
    AMMUNITION: 8,
};
// Default properties for all item types
export const ITEM_PROPERTIES = {
    [ItemType.NONE]: {
        name: 'None',
        category: ItemCategory.NONE,
        stackable: false,
        maxStack: 1,
        weight: 0,
        value: 0,
    },
    // Raw materials
    [ItemType.WOOD]: {
        name: 'Wood',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 100,
        weight: 1,
        value: 2,
    },
    [ItemType.STONE]: {
        name: 'Stone',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 100,
        weight: 2,
        value: 1,
    },
    [ItemType.IRON_ORE]: {
        name: 'Iron Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 3,
        value: 10,
    },
    [ItemType.GOLD_ORE]: {
        name: 'Gold Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 4,
        value: 50,
    },
    [ItemType.COPPER_ORE]: {
        name: 'Copper Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 3,
        value: 8,
    },
    [ItemType.COAL]: {
        name: 'Coal',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 100,
        weight: 1,
        value: 3,
    },
    [ItemType.TIN_ORE]: {
        name: 'Tin Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 2,
        value: 5,
    },
    [ItemType.SILVER_ORE]: {
        name: 'Silver Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 4,
        value: 25,
    },
    [ItemType.MITHRIL_ORE]: {
        name: 'Mithril Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 2,
        value: 100,
    },
    [ItemType.STARMETAL_ORE]: {
        name: 'Starmetal Ore',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 3,
        value: 250,
    },
    [ItemType.BONE]: {
        name: 'Bone',
        category: ItemCategory.RAW_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 0.5,
        value: 3,
    },
    // Processed materials
    [ItemType.PLANK]: {
        name: 'Plank',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 1,
        value: 5,
    },
    [ItemType.IRON_BAR]: {
        name: 'Iron Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 4,
        value: 25,
    },
    [ItemType.GOLD_BAR]: {
        name: 'Gold Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 5,
        value: 150,
    },
    [ItemType.COPPER_BAR]: {
        name: 'Copper Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 4,
        value: 20,
    },
    [ItemType.STEEL_BAR]: {
        name: 'Steel Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 5,
        value: 40,
    },
    [ItemType.TIN_BAR]: {
        name: 'Tin Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 3,
        value: 12,
    },
    [ItemType.BRONZE_BAR]: {
        name: 'Bronze Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 4,
        value: 18,
    },
    [ItemType.SILVER_BAR]: {
        name: 'Silver Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 5,
        value: 75,
    },
    [ItemType.MITHRIL_BAR]: {
        name: 'Mithril Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 3,
        value: 300,
    },
    [ItemType.STARMETAL_BAR]: {
        name: 'Starmetal Bar',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 25,
        weight: 4,
        value: 750,
    },
    // Food
    [ItemType.BERRIES]: {
        name: 'Berries',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 50,
        weight: 0.1,
        value: 1,
    },
    [ItemType.RAW_MEAT]: {
        name: 'Raw Meat',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 1,
        value: 5,
    },
    [ItemType.COOKED_MEAT]: {
        name: 'Cooked Meat',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 1,
        value: 10,
    },
    [ItemType.BREAD]: {
        name: 'Bread',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.5,
        value: 8,
    },
    // Fish - Raw
    [ItemType.RAW_MINNOW]: {
        name: 'Raw Minnow',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.2,
        value: 2,
    },
    [ItemType.RAW_SHRIMP]: {
        name: 'Raw Shrimp',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.3,
        value: 9,
    },
    [ItemType.RAW_TROUT]: {
        name: 'Raw Trout',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.5,
        value: 5,
    },
    [ItemType.RAW_COD]: {
        name: 'Raw Cod',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.6,
        value: 7,
    },
    [ItemType.RAW_SALMON]: {
        name: 'Raw Salmon',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.7,
        value: 10,
    },
    [ItemType.RAW_SWAMP_EEL]: {
        name: 'Raw Swamp Eel',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.6,
        value: 12,
    },
    // Fish - Cooked
    [ItemType.COOKED_MINNOW]: {
        name: 'Cooked Minnow',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.2,
        value: 5,
    },
    [ItemType.COOKED_SHRIMP]: {
        name: 'Cooked Shrimp',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.3,
        value: 16,
    },
    [ItemType.COOKED_TROUT]: {
        name: 'Cooked Trout',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.5,
        value: 12,
    },
    [ItemType.COOKED_COD]: {
        name: 'Cooked Cod',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.6,
        value: 15,
    },
    [ItemType.COOKED_SALMON]: {
        name: 'Cooked Salmon',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.7,
        value: 20,
    },
    [ItemType.COOKED_EEL]: {
        name: 'Cooked Eel',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 20,
        weight: 0.6,
        value: 25,
    },
    // Fish - Advanced
    [ItemType.FISH_STEW]: {
        name: 'Fish Stew',
        category: ItemCategory.FOOD,
        stackable: true,
        maxStack: 10,
        weight: 1.0,
        value: 30,
    },
    // Tools
    [ItemType.WOODEN_AXE]: {
        name: 'Wooden Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 10,
    },
    [ItemType.STONE_AXE]: {
        name: 'Stone Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 25,
    },
    [ItemType.IRON_AXE]: {
        name: 'Iron Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 75,
    },
    [ItemType.BRONZE_AXE]: {
        name: 'Bronze Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 50,
    },
    [ItemType.STEEL_AXE]: {
        name: 'Steel Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 5,
        value: 120,
    },
    [ItemType.MITHRIL_AXE]: {
        name: 'Mithril Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 400,
    },
    [ItemType.STARMETAL_AXE]: {
        name: 'Starmetal Axe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 750,
    },
    [ItemType.WOODEN_PICKAXE]: {
        name: 'Wooden Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 10,
    },
    [ItemType.STONE_PICKAXE]: {
        name: 'Stone Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 25,
    },
    [ItemType.IRON_PICKAXE]: {
        name: 'Iron Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 75,
    },
    [ItemType.BRONZE_PICKAXE]: {
        name: 'Bronze Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 50,
    },
    [ItemType.STEEL_PICKAXE]: {
        name: 'Steel Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 5,
        value: 120,
    },
    [ItemType.SILVER_PICKAXE]: {
        name: 'Silver Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 5,
        value: 200,
    },
    [ItemType.MITHRIL_PICKAXE]: {
        name: 'Mithril Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 500,
    },
    [ItemType.STARMETAL_PICKAXE]: {
        name: 'Starmetal Pickaxe',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 4,
        value: 1200,
    },
    // Fishing Rods
    [ItemType.WOODEN_FISHING_ROD]: {
        name: 'Wooden Fishing Rod',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 1.5,
        value: 10,
    },
    [ItemType.COPPER_FISHING_ROD]: {
        name: 'Copper Fishing Rod',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 30,
    },
    [ItemType.IRON_FISHING_ROD]: {
        name: 'Iron Fishing Rod',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 2.5,
        value: 75,
    },
    [ItemType.STEEL_FISHING_ROD]: {
        name: 'Steel Fishing Rod',
        category: ItemCategory.TOOL,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 150,
    },
    // Weapons
    [ItemType.WOODEN_SWORD]: {
        name: 'Wooden Sword',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 1,
        value: 5,
    },
    [ItemType.STONE_SWORD]: {
        name: 'Stone Sword',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 20,
    },
    [ItemType.IRON_SWORD]: {
        name: 'Iron Sword',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 60,
    },
    // Ranged Weapons
    [ItemType.WOODEN_BOW]: {
        name: 'Wooden Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 1.5,
        value: 15,
    },
    [ItemType.STONE_BOW]: {
        name: 'Stone Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 30,
    },
    [ItemType.IRON_BOW]: {
        name: 'Iron Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2.5,
        value: 80,
    },
    [ItemType.BRONZE_BOW]: {
        name: 'Bronze Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2.5,
        value: 60,
    },
    [ItemType.STEEL_BOW]: {
        name: 'Steel Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 150,
    },
    [ItemType.SILVER_BOW]: {
        name: 'Silver Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 3,
        value: 250,
    },
    [ItemType.MITHRIL_BOW]: {
        name: 'Mithril Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 600,
    },
    [ItemType.STARMETAL_BOW]: {
        name: 'Starmetal Bow',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2.5,
        value: 1500,
    },
    [ItemType.FLINTLOCK_PISTOL]: {
        name: 'Flintlock Pistol',
        category: ItemCategory.WEAPON,
        stackable: false,
        maxStack: 1,
        weight: 2,
        value: 200,
    },
    // Ammunition
    [ItemType.WOODEN_ARROW]: {
        name: 'Wooden Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.05,
        value: 1,
    },
    [ItemType.STONE_ARROW]: {
        name: 'Stone Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.1,
        value: 2,
    },
    [ItemType.IRON_ARROW]: {
        name: 'Iron Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.1,
        value: 5,
    },
    [ItemType.BRONZE_ARROW]: {
        name: 'Bronze Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.1,
        value: 4,
    },
    [ItemType.STEEL_ARROW]: {
        name: 'Steel Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.1,
        value: 8,
    },
    [ItemType.SILVER_ARROW]: {
        name: 'Silver Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.1,
        value: 15,
    },
    [ItemType.MITHRIL_ARROW]: {
        name: 'Mithril Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.05,
        value: 30,
    },
    [ItemType.STARMETAL_ARROW]: {
        name: 'Starmetal Arrow',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 99,
        weight: 0.08,
        value: 75,
    },
    [ItemType.LEAD_BALL]: {
        name: 'Lead Ball',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 50,
        weight: 0.2,
        value: 3,
    },
    [ItemType.GUNPOWDER]: {
        name: 'Gunpowder',
        category: ItemCategory.AMMUNITION,
        stackable: true,
        maxStack: 50,
        weight: 0.1,
        value: 5,
    },
    // Crafting materials
    [ItemType.CLOTH]: {
        name: 'Cloth',
        category: ItemCategory.PROCESSED_MATERIAL,
        stackable: true,
        maxStack: 50,
        weight: 0.2,
        value: 8,
    },
};
// Helper to get item properties
export const getItemProperties = ({ itemType }) => {
    return ITEM_PROPERTIES[itemType] ?? ITEM_PROPERTIES[ItemType.NONE];
};
// Helper to get item name for display
export const getItemName = ({ itemType }) => {
    return getItemProperties({ itemType }).name;
};
// Mapping from resource yield type to item type
export const RESOURCE_TO_ITEM = {
    wood: ItemType.WOOD,
    stone: ItemType.STONE,
    iron_ore: ItemType.IRON_ORE,
    gold_ore: ItemType.GOLD_ORE,
    copper_ore: ItemType.COPPER_ORE,
    berries: ItemType.BERRIES,
    coal: ItemType.COAL,
    tin_ore: ItemType.TIN_ORE,
    silver_ore: ItemType.SILVER_ORE,
    mithril_ore: ItemType.MITHRIL_ORE,
    starmetal_ore: ItemType.STARMETAL_ORE,
    // Processed materials for construction
    metal: ItemType.IRON_BAR,
    iron_bar: ItemType.IRON_BAR,
    cloth: ItemType.CLOTH,
};
// Get item type from resource string
export const getItemTypeFromResource = ({ resourceType, }) => {
    return RESOURCE_TO_ITEM[resourceType] ?? ItemType.NONE;
};
// Mapping from item type to icon filename
const ITEM_ICONS = {
    // Raw materials
    [ItemType.WOOD]: 'wood.png',
    [ItemType.STONE]: 'stone.png',
    [ItemType.IRON_ORE]: 'iron_ore.png',
    [ItemType.GOLD_ORE]: 'gold_ore.png',
    [ItemType.COPPER_ORE]: 'copper_ore.png',
    [ItemType.COAL]: 'coal.png',
    [ItemType.TIN_ORE]: 'tin_ore.png',
    [ItemType.SILVER_ORE]: 'silver_ore.png',
    [ItemType.MITHRIL_ORE]: 'mithril_ore.png',
    [ItemType.STARMETAL_ORE]: 'starmetal_ore.png',
    [ItemType.BONE]: 'bone.png',
    // Processed materials
    [ItemType.PLANK]: 'plank.png',
    [ItemType.IRON_BAR]: 'iron_bar.png',
    [ItemType.GOLD_BAR]: 'gold_bar.png',
    [ItemType.COPPER_BAR]: 'copper_bar.png',
    [ItemType.STEEL_BAR]: 'steel_bar.png',
    [ItemType.TIN_BAR]: 'tin_bar.png',
    [ItemType.BRONZE_BAR]: 'bronze_bar.png',
    [ItemType.SILVER_BAR]: 'silver_bar.png',
    [ItemType.MITHRIL_BAR]: 'mithril_bar.png',
    [ItemType.STARMETAL_BAR]: 'starmetal_bar.png',
    // Food
    [ItemType.BERRIES]: 'berries.png',
    [ItemType.RAW_MEAT]: 'raw_meat.png',
    [ItemType.COOKED_MEAT]: 'cooked_meat.png',
    [ItemType.BREAD]: 'bread.png',
    [ItemType.RAW_MINNOW]: 'raw_minnow.png',
    [ItemType.RAW_SHRIMP]: 'raw_shrimp.png',
    [ItemType.RAW_TROUT]: 'raw_trout.png',
    [ItemType.RAW_COD]: 'raw_cod.png',
    [ItemType.RAW_SALMON]: 'raw_salmon.png',
    [ItemType.RAW_SWAMP_EEL]: 'raw_swamp_eel.png',
    [ItemType.COOKED_MINNOW]: 'cooked_minnow.png',
    [ItemType.COOKED_SHRIMP]: 'cooked_shrimp.png',
    [ItemType.COOKED_TROUT]: 'cooked_trout.png',
    [ItemType.COOKED_COD]: 'cooked_cod.png',
    [ItemType.COOKED_SALMON]: 'cooked_salmon.png',
    [ItemType.COOKED_EEL]: 'cooked_eel.png',
    [ItemType.FISH_STEW]: 'fish_stew.png',
    // Fishing Rods
    [ItemType.WOODEN_FISHING_ROD]: 'wooden_fishing_rod.png',
    [ItemType.COPPER_FISHING_ROD]: 'copper_fishing_rod.png',
    [ItemType.IRON_FISHING_ROD]: 'iron_fishing_rod.png',
    [ItemType.STEEL_FISHING_ROD]: 'steel_fishing_rod.png',
    // Pickaxes
    [ItemType.WOODEN_PICKAXE]: 'wooden_pickaxe.png',
    [ItemType.STONE_PICKAXE]: 'stone_pickaxe.png',
    [ItemType.IRON_PICKAXE]: 'iron_pickaxe.png',
    [ItemType.BRONZE_PICKAXE]: 'bronze_pickaxe.png',
    [ItemType.STEEL_PICKAXE]: 'steel_pickaxe.png',
    [ItemType.SILVER_PICKAXE]: 'silver_pickaxe.png',
    [ItemType.MITHRIL_PICKAXE]: 'mithril_pickaxe.png',
    [ItemType.STARMETAL_PICKAXE]: 'starmetal_pickaxe.png',
    // Melee Weapons
    [ItemType.WOODEN_SWORD]: 'wooden_sword.png',
    [ItemType.STONE_SWORD]: 'stone_sword.png',
    [ItemType.IRON_SWORD]: 'iron_sword.png',
    // Axes
    [ItemType.WOODEN_AXE]: 'wooden_axe.png',
    [ItemType.STONE_AXE]: 'stone_axe.png',
    [ItemType.IRON_AXE]: 'iron_axe.png',
    [ItemType.BRONZE_AXE]: 'bronze_axe.png',
    [ItemType.STEEL_AXE]: 'steel_axe.png',
    [ItemType.MITHRIL_AXE]: 'mithril_axe.png',
    [ItemType.STARMETAL_AXE]: 'starmetal_axe.png',
    // Ranged Weapons
    [ItemType.WOODEN_BOW]: 'wooden_bow.png',
    [ItemType.STONE_BOW]: 'stone_bow.png',
    [ItemType.IRON_BOW]: 'iron_bow.png',
    [ItemType.BRONZE_BOW]: 'bronze_bow.png',
    [ItemType.STEEL_BOW]: 'steel_bow.png',
    [ItemType.SILVER_BOW]: 'silver_bow.png',
    [ItemType.MITHRIL_BOW]: 'mithril_bow.png',
    [ItemType.STARMETAL_BOW]: 'starmetal_bow.png',
    [ItemType.FLINTLOCK_PISTOL]: 'flintlock_pistol.png',
    // Ammunition
    [ItemType.WOODEN_ARROW]: 'wooden_arrow.png',
    [ItemType.STONE_ARROW]: 'stone_arrow.png',
    [ItemType.IRON_ARROW]: 'iron_arrow.png',
    [ItemType.BRONZE_ARROW]: 'bronze_arrow.png',
    [ItemType.STEEL_ARROW]: 'steel_arrow.png',
    [ItemType.SILVER_ARROW]: 'silver_arrow.png',
    [ItemType.MITHRIL_ARROW]: 'mithril_arrow.png',
    [ItemType.STARMETAL_ARROW]: 'starmetal_arrow.png',
    [ItemType.LEAD_BALL]: 'lead_ball.png',
    [ItemType.GUNPOWDER]: 'gunpowder.png',
};
// Get icon path for an item type
export const getItemIcon = ({ itemType }) => {
    return ITEM_ICONS[itemType] ?? null;
};
// ============================================================================
// Equipment System
// ============================================================================
// Equipment slots that items can be equipped to
export const EquipmentSlot = {
    NONE: 0,
    PRIMARY: 1, // Main weapon (swords, bows, pistols)
    HEADGEAR: 2, // Head armor
    TORSO: 3, // Chest armor
    LEGS: 4, // Leg armor
    UTILITY: 5, // Utility items
};
const EQUIPPABLE_TOOL_ITEM_TYPES = new Set([
    ItemType.WOODEN_PICKAXE,
    ItemType.STONE_PICKAXE,
    ItemType.IRON_PICKAXE,
    ItemType.BRONZE_PICKAXE,
    ItemType.STEEL_PICKAXE,
    ItemType.SILVER_PICKAXE,
    ItemType.MITHRIL_PICKAXE,
    ItemType.STARMETAL_PICKAXE,
    ItemType.WOODEN_AXE,
    ItemType.STONE_AXE,
    ItemType.IRON_AXE,
    ItemType.BRONZE_AXE,
    ItemType.STEEL_AXE,
    ItemType.MITHRIL_AXE,
    ItemType.STARMETAL_AXE,
]);
// Check if an item can be equipped
export const isEquippableItem = ({ itemType }) => {
    const properties = getItemProperties({ itemType });
    if (properties.category === ItemCategory.WEAPON || properties.category === ItemCategory.ARMOR) {
        return true;
    }
    return EQUIPPABLE_TOOL_ITEM_TYPES.has(itemType);
};
// Get which equipment slot an item goes into
export const getEquipmentSlotForItem = ({ itemType, }) => {
    const properties = getItemProperties({ itemType });
    // Weapons go to PRIMARY slot
    if (properties.category === ItemCategory.WEAPON) {
        return EquipmentSlot.PRIMARY;
    }
    if (EQUIPPABLE_TOOL_ITEM_TYPES.has(itemType)) {
        return EquipmentSlot.UTILITY;
    }
    // TODO: When armor items are added, map them to appropriate slots
    // HEADGEAR, TORSO, LEGS based on item type ranges
    return EquipmentSlot.NONE;
};
// Get equipment slot name for display
export const getEquipmentSlotName = ({ slot }) => {
    switch (slot) {
        case EquipmentSlot.PRIMARY:
            return 'Weapon';
        case EquipmentSlot.HEADGEAR:
            return 'Head';
        case EquipmentSlot.TORSO:
            return 'Torso';
        case EquipmentSlot.LEGS:
            return 'Legs';
        case EquipmentSlot.UTILITY:
            return 'Utility';
        default:
            return 'Unknown';
    }
};
//# sourceMappingURL=types.js.map